import { useEffect, useMemo } from 'react';
import Select from '@Common/Select';
import { useGeneratorContext } from '@Context/GeneratorContext';
import { Product, Project } from '@Models/types';
import { useAuth } from '@Context/CognitoAuthProvider';
import { ahaAPI } from '@Lib/axios/aha';
import PromptCrud from './prompt-crud/PromptCrud';
import Header from '../tabs/Header';

interface ProductsProps {
  showPromptCrud?: boolean;
  onModeSwitch?: () => void;
}

const Products = ({ showPromptCrud = false, onModeSwitch }: ProductsProps) => {
  const { user, logout } = useAuth();
  const aha = useMemo(() => ahaAPI(logout), [logout]);
  const {
    products,
    fetchProducts,
    selectedProduct,
    setSelectedProduct,
    genPageLoading,
    setGenPageLoading,
  } = useGeneratorContext();

  const loading = genPageLoading;

  useEffect(() => {
    if (user?.profile && products.length === 0) {
      setGenPageLoading(true);
      fetchProducts().finally(() => setGenPageLoading(false));
    }
  }, [user, fetchProducts, products.length, setGenPageLoading]);

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        label: product.name,
        value: product,
      })),
    [products]
  );

  const handleProductChange = (
    selected: { label: string; value: Product } | null
  ) => {
    setSelectedProduct(selected?.value || null);
  };

  const placeholder =
    loading && !selectedProduct
      ? 'Loading Aha! Pods'
      : selectedProduct?.name || 'Select Aha! Pod';

  const favorites = useMemo(
    () => ({
      enabled: true,
      type: 'product' as const,
      fetch: aha.getFavProd,
      save: aha.saveFavProd as (items: Product[] | Project[]) => Promise<void>,
      favoriteKey: 'reference_prefix' as const,
    }),
    [aha]
  );

  return (
    <div data-testid="test-products" className="rounded-lg bg-white p-4">
      {showPromptCrud ? (
        <div className="grid grid-cols-2 gap-x-4">
          <div className="flex flex-col gap-y-2">
            <Header
              title="Aha! Pods"
              onModeSwitch={onModeSwitch}
              isJiraActive={false}
            />
            <Select
              type="product"
              options={productOptions}
              onChange={handleProductChange}
              placeholder={placeholder}
              loading={loading}
              disabled={loading}
              favorites={favorites}
            />
          </div>
          <PromptCrud />
        </div>
      ) : (
        <div className="flex flex-col gap-y-2">
          <span className="font-extrabold text-primary-700">Aha! Pods</span>
          <Select
            type="product"
            options={productOptions}
            onChange={handleProductChange}
            placeholder={placeholder}
            loading={loading}
            disabled={loading}
            favorites={favorites}
          />
        </div>
      )}
    </div>
  );
};

export default Products;
